import { createContext, useCallback, useState, useMemo } from "react";
import kebabCase from "lodash/kebabCase";
import { ToastData, types as toastTypes } from "../../components/Toast";
import { ToastContextApi } from "./types";

export const ToastsContext = createContext<ToastContextApi | undefined>(undefined);

export const ToastsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastContextApi["toasts"]>([]);

  const toast = useCallback(
    ({ title, description, type, icon }: Omit<ToastData, "id">) => {
      setToasts((prevToasts) => {
        const id = kebabCase(title);

        // Remove any existing toasts with the same id
        const currentToasts = prevToasts.filter((prevToast) => prevToast.id !== id);

        return [
          {
            id,
            title,
            description,
            type,
            icon,
          },
          ...currentToasts,
        ];
      });
    },
    [setToasts]
  );

  const toastError = useCallback(
    (title: ToastData["title"], description?: ToastData["description"], icon?: ToastData["icon"]) => {
      return toast({ title, description, type: toastTypes.DANGER, icon });
    },
    [toast]
  );

  const toastInfo = useCallback(
    (title: ToastData["title"], description?: ToastData["description"], icon?: ToastData["icon"]) => {
      return toast({ title, description, type: toastTypes.INFO, icon });
    },
    [toast]
  );

  const toastSuccess = useCallback(
    (title: ToastData["title"], description?: ToastData["description"], icon?: ToastData["icon"]) => {
      return toast({ title, description, type: toastTypes.SUCCESS, icon });
    },
    [toast]
  );

  const toastWarning = useCallback(
    (title: ToastData["title"], description?: ToastData["description"], icon?: ToastData["icon"]) => {
      return toast({ title, description, type: toastTypes.WARNING, icon });
    },
    [toast]
  );

  const toastCustom = useCallback(
    (description: ToastData["description"]) => {
      return toast({ title: "", description, type: toastTypes.CUSTOM });
    },
    [toast]
  );

  const clear = useCallback(() => setToasts([]), []);
  const remove = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((prevToast) => prevToast.id !== id));
  }, []);

  const providerValue = useMemo(() => {
    return { toasts, clear, remove, toastError, toastInfo, toastSuccess, toastWarning, toastCustom };
  }, [toasts, clear, remove, toastError, toastInfo, toastSuccess, toastWarning, toastCustom]);

  return <ToastsContext.Provider value={providerValue}>{children}</ToastsContext.Provider>;
};
